import { Fragment, memo, useEffect, useMemo, useRef } from 'react';

import { IMessage } from '@/shared/api/types/chat-meesage';
import { Loading } from '@/shared/ui/loading';

import Box from '../../ui/box';
import CustomReactMarkdown from '../custom-markdown/custom-markdown';
import { MessageWithDelay } from '../message-with-delay/message-with-delay';

interface MessageListProps {
  messages: IMessage[];
  botMessageBufferMap?: Record<string, IMessage>;
  processingMap?: Record<string, boolean | string>;
}

const removeCustomBraces = (text: string): string => {
  return text.replace(/<node>.*?<\/node>/g, '');
};

const MessagesList = ({ messages, botMessageBufferMap, processingMap }: MessageListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, botMessageBufferMap]);

  const botMessages = useMemo(() => {
    return Object.values(botMessageBufferMap || {});
  }, [botMessageBufferMap]);

  const filteredBotMessages = botMessages.filter((msg) => !messages.find((m) => m.id === msg.id));
  const allMessages: IMessage[] = [...messages, ...filteredBotMessages].sort(
    (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
  );

  return (
    <Box>
      {allMessages.map((message) => {
        const isProcessing = !!processingMap?.[message.id];

        const lastMessage = allMessages[allMessages.length - 1];
        const isWaitingForBot =
          lastMessage?.sender === 'user' &&
          !allMessages.some(
            (m) => m.sender === 'bot' && (m.createdAt || 0) > (lastMessage?.createdAt || 0),
          );

        return (
          <Fragment key={message.id}>
            {message.sender === 'user' && (
              <div className="group relative flex items-center justify-between gap-2 mb-8">
                <div className="relative flex flex-col gap-1 bg-[#c7e3ff] px-[10px] py-[5px] rounded-lg max-w-full">
                  <span className="leading-[20px] text-black">
                    <CustomReactMarkdown>
                      {removeCustomBraces(message.message as string)}
                    </CustomReactMarkdown>
                  </span>
                  <div className="absolute bottom-[-20px] left-0 text-[#666666] text-[14px] font-semibold">
                    Вы
                  </div>
                </div>
              </div>
            )}
            {isWaitingForBot && (
              <div className="group relative flex items-center justify-end gap-2 mb-8">
                <div className="relative flex flex-col gap-1">
                  <div className="bg-[#F7F7F7] px-[10px] py-[5px] rounded-lg max-w-[340px] flex items-center gap-2">
                    <Loading visible w={18} />
                    <span className="text-[#666] text-sm font-medium">Ждём ответа нейросети…</span>
                  </div>
                </div>
              </div>
            )}
            {message.sender === 'bot' && (
              <div className="group relative flex items-center justify-end gap-2 mb-8">
                <div className="relative flex flex-col gap-1">
                  <div
                    className={`${message.isError ? 'bg-[#ffd0d0] border-2 border-[#ff7e7e]' : 'bg-[#F7F7F7]'}
    px-[10px] py-[5px] rounded-lg max-w-[340px]`}
                  >
                    <span className="leading-[20px] text-black max-w-full">
                      {!!message.message?.trim() && (
                        <MessageWithDelay
                          text={message.message}
                          delay={10}
                          withMarkdown
                          isError={message.isError}
                        />
                      )}
                      <div ref={messagesEndRef} />
                    </span>
                  </div>
                  <Box className="absolute bottom-[-20px] right-0 text-[#666666] text-[14px] font-semibold">
                    <Box className="flex flex-row w-fit gap-[10px] items-center">
                      {isProcessing && <Loading visible w={14} />}
                      <Box>Нейросеть</Box>
                    </Box>
                  </Box>
                </div>
              </div>
            )}
          </Fragment>
        );
      })}
    </Box>
  );
};

export default memo(MessagesList);
